import React, { useEffect, useState, useCallback } from 'react';
import { Card, Spinner, Alert, Form, Row, Col, Button, Badge } from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';

const AdminTermsConfig = () => {
  const { getAuthHeaders } = useAuthHeaders();

  // Versión vigente
  const [current, setCurrent] = useState(null);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [currentError, setCurrentError] = useState(null);

  // Feature flag (config Terms:Enabled), leído del estado
  const [enabled, setEnabled] = useState(null);

  // Publicar nueva versión
  const [version, setVersion] = useState('');
  const [contentText, setContentText] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [elementId, setElementId] = useState('terminos-contenido');
  const [effectiveAt, setEffectiveAt] = useState(''); // datetime-local
  const [publishing, setPublishing] = useState(false);
  const [publishFeedback, setPublishFeedback] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchFeedback, setFetchFeedback] = useState(null);

  const fetchCurrent = useCallback(async () => {
    setCurrentLoading(true);
    setCurrentError(null);
    try {
      // GET /terminos/vigente es AllowAnonymous; no requiere headers.
      const res = await fetch(`${API_BASE}/terminos/vigente`);
      if (res.status === 404) {
        setCurrent(null);
        return;
      }
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setCurrent(await res.json());
    } catch (err) {
      setCurrentError(err.message);
    } finally {
      setCurrentLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/terminos/estado`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEnabled(!!data.enabled);
      }
    } catch {
      // Silencioso: el badge simplemente no se muestra.
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCurrent(); fetchStatus(); }, [fetchCurrent, fetchStatus]);

  const handleFetchText = async () => {
    if (!documentUrl.trim()) return;
    setFetching(true);
    setFetchFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/terminos/obtener-texto`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: documentUrl.trim(), elementId: elementId.trim() || null }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setFetchFeedback({ ok: false, message: data?.message || `Error ${res.status}` });
        return;
      }
      setContentText(data.text || '');
      setFetchFeedback({ ok: true, message: 'Texto obtenido de la URL. Revísalo antes de publicar.' });
    } catch (err) {
      setFetchFeedback({ ok: false, message: err.message });
    } finally {
      setFetching(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setPublishFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const body = {
        version: version.trim(),
        contentText: contentText.trim() || null,
        documentUrl: documentUrl.trim() || null,
        effectiveAtUtc: effectiveAt ? new Date(effectiveAt).toISOString() : null,
      };
      const res = await fetch(`${API_BASE}/terminos/versiones`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setPublishFeedback({ ok: false, message: data?.message || `Error ${res.status}` });
        return;
      }
      setPublishFeedback({
        ok: true,
        message: `Versión ${data?.version || version} publicada como vigente. Las cuentas con una versión anterior deberán re-aceptar.`,
      });
      setVersion('');
      setContentText('');
      setDocumentUrl('');
      setEffectiveAt('');
      await fetchCurrent();
    } catch (err) {
      setPublishFeedback({ ok: false, message: err.message });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">
            Admin — Términos y Condiciones
            {enabled !== null && (
              <Badge bg={enabled ? 'success' : 'secondary'} className="ms-2 align-middle" style={{ fontSize: '0.7rem' }}>
                {enabled ? 'Activada' : 'Desactivada'}
              </Badge>
            )}
          </h2>
          <p className="page-subtitle text-muted">
            Publica una versión nueva de los T&amp;C. Al publicar, la versión anterior deja de estar
            vigente y las cuentas que solo la tenían aceptada vuelven a estado pendiente automáticamente.
          </p>
          {enabled === false && (
            <Alert variant="warning" className="py-2 small mb-0">
              La aplicación de T&amp;C está <strong>desactivada</strong> (feature flag <code>Terms:Enabled=false</code>):
              no se muestra el modal ni se bloquea a nadie. Puedes preparar/publicar versiones aquí;
              actívala en la config cuando estés listo.
            </Alert>
          )}
        </div>
      </div>

      {/* Versión vigente */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white">
          <strong>Versión vigente</strong>
        </Card.Header>
        <Card.Body>
          {currentError && <Alert variant="danger" className="mb-0">Error: {currentError}</Alert>}
          {currentLoading ? (
            <Spinner animation="border" size="sm" />
          ) : current ? (
            <div>
              <div className="mb-2">
                <Badge bg="success" className="me-2">{current.version}</Badge>
                {current.effectiveAtUtc && (
                  <span className="text-muted small">
                    Vigente desde {new Date(current.effectiveAtUtc).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="small text-muted">
                <div className="text-break"><strong>Hash:</strong> <code>{current.hash}</code></div>
                {current.url && (
                  <div className="mt-1">
                    <strong>URL:</strong>{' '}
                    <a href={current.url} target="_blank" rel="noopener noreferrer">{current.url}</a>
                  </div>
                )}
              </div>
              {current.text && (
                <details className="mt-3">
                  <summary className="small text-primary" style={{ cursor: 'pointer' }}>Ver texto</summary>
                  <div
                    className="border rounded p-3 mt-2 bg-light"
                    style={{ maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}
                  >
                    {current.text}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <Alert variant="warning" className="mb-0">
              No hay una versión vigente configurada. Publica la primera abajo.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Publicar nueva versión */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white">
          <strong>Publicar nueva versión</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">Versión</Form.Label>
              <Form.Control
                size="sm"
                placeholder="ej. v2.0"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={publishing}
              />
              <Form.Text className="text-muted">Debe ser única.</Form.Text>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">Vigente desde (opcional)</Form.Label>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={effectiveAt}
                onChange={(e) => setEffectiveAt(e.target.value)}
                disabled={publishing}
              />
              <Form.Text className="text-muted">Default: ahora.</Form.Text>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">URL del documento (opcional)</Form.Label>
              <Form.Control
                size="sm"
                placeholder="https://sat-go.com/terminos"
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
                disabled={publishing}
              />
            </Col>

            {/* Obtener el texto directamente de la URL publicada */}
            <Col xs={12}>
              <div className="p-3 border rounded bg-light">
                <Row className="g-2 align-items-end">
                  <Col xs={12} md={5}>
                    <Form.Label className="small mb-1">Id del elemento a extraer</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="terminos-contenido"
                      value={elementId}
                      onChange={(e) => setElementId(e.target.value)}
                      disabled={publishing || fetching}
                    />
                    <Form.Text className="text-muted">
                      Se extrae el texto de ese contenedor de la página (default: <code>terminos-contenido</code>).
                    </Form.Text>
                  </Col>
                  <Col xs={12} md={4}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100"
                      onClick={handleFetchText}
                      disabled={fetching || publishing || !documentUrl.trim()}
                    >
                      {fetching ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Obteniendo…</>) : 'Obtener texto de la URL'}
                    </Button>
                  </Col>
                </Row>
                {fetchFeedback && (
                  <div className={`small mt-2 ${fetchFeedback.ok ? 'text-success' : 'text-danger'}`}>
                    {fetchFeedback.message}
                  </div>
                )}
              </div>
            </Col>

            <Col xs={12}>
              <Form.Label className="small mb-1">Texto de los Términos y Condiciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                disabled={publishing}
                placeholder="Pega aquí el texto legal completo. El hash SHA256 (evidencia de aceptación) se calcula sobre este texto."
              />
              <Form.Text className="text-muted">
                Este es el texto que verá el cliente al aceptar y sobre el que se calcula el hash de evidencia.
              </Form.Text>
            </Col>
          </Row>

          {publishFeedback && (
            <Alert variant={publishFeedback.ok ? 'success' : 'danger'} className="mt-3 mb-0">
              {publishFeedback.message}
            </Alert>
          )}
        </Card.Body>
        <Card.Footer className="bg-white d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handlePublish}
            disabled={publishing || !version.trim()}
          >
            {publishing ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Publicando…</>) : 'Publicar como vigente'}
          </Button>
        </Card.Footer>
      </Card>

      {/* El envío del correo de aceptación (con enlace único por destinatario) vive en
          "Enviar Correos" → plantilla "Aceptación de Términos y Condiciones". */}
      <Alert variant="light" className="border small text-muted">
        Para enviar el correo de aceptación a los clientes, ve a{' '}
        <strong>Enviar Correos</strong> y elige la plantilla{' '}
        <strong>“Aceptación de Términos y Condiciones”</strong>: genera un enlace único por destinatario.
      </Alert>
    </div>
  );
};

export default AdminTermsConfig;
